/**
 * @file LabelSheetTemplateCard.tsx
 * @description Карточка калибровки одного листа самоклейки: поля и зазоры в
 *   мм, миниатюра раскладки и «Пробная страница» (phase19.22, W17-5a/5b;
 *   ui-design §3, решение пользователя п.7 «поля + зазоры на шаблон склада»).
 *
 * Пробная страница печатается с ТЕКУЩИМИ значениями формы (ещё не
 * сохранёнными): положил лист на просвет — поправил — снова напечатал.
 * Свёрнута на телефоне у всех, кроме первой: три карточки полей подряд
 * уводили «Сохранить» на третий экран.
 */

import { ChevronDown, FileText, Loader2, RotateCcw } from "lucide-react";
import { useState } from "react";

import { LabelSheetMiniPreview } from "./LabelSheetMiniPreview";
import { MARGIN_FIELDS, draftWarning, fromDraft, toDraft, type MarginDraft } from "./label-sheet-form";
import { Button } from "../../ui/button";
import type { LabelSheetDto } from "../../../lib/api-labels";
import { cn } from "@/lib/utils";

interface Props {
  sheet: LabelSheetDto;
  draft: MarginDraft;
  onChange: (draft: MarginDraft) => void;
  /** Пробная страница по текущим значениям; `false` — сервис печати недоступен. */
  onCalibrate: () => Promise<void>;
  defaultOpen: boolean;
  readOnly: boolean;
}

export function LabelSheetTemplateCard({ sheet, draft, onChange, onCalibrate, defaultOpen, readOnly }: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const [calibrating, setCalibrating] = useState(false);
  const warning = draftWarning(draft);
  const margins = fromDraft(draft) ?? sheet;
  const changedFromDefault = JSON.stringify(draft) !== JSON.stringify(toDraft(sheet.defaults));

  async function calibrate() {
    setCalibrating(true);
    await onCalibrate();
    setCalibrating(false);
  }

  return (
    <section className="flex flex-col gap-3 rounded-xl bg-surface-lowest p-4 ring-ghost">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex min-h-12 items-center justify-between gap-2 text-left lg:pointer-events-none"
      >
        <span className="flex flex-col">
          <span className="text-sm font-medium">{sheet.title}</span>
          <span className="text-xs text-on-surface-variant">
            {sheet.perSheet} на листе · {sheet.cols} × {sheet.rows}
            {sheet.customized ? " · откалиброван" : ""}
          </span>
        </span>
        <ChevronDown className={cn("size-5 transition-transform lg:hidden", open && "rotate-180")} aria-hidden />
      </button>

      <div className={cn("flex-col gap-3", open ? "flex" : "hidden lg:flex")}>
        <div className="flex gap-3">
          <div className="grid min-w-0 flex-1 grid-cols-2 gap-2">
            {MARGIN_FIELDS.map(({ key, label }) => (
              <label key={key} className="flex flex-col gap-1">
                <span className="text-xs text-on-surface-variant">{label}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={draft[key]}
                  onChange={(e) => onChange({ ...draft, [key]: e.target.value })}
                  disabled={readOnly}
                  className="min-h-14 w-full rounded-lg border border-on-surface/15 bg-surface-lowest px-3 font-mono text-base"
                />
              </label>
            ))}
          </div>
          <div className="hidden sm:block">
            <LabelSheetMiniPreview sheet={sheet} margins={margins} />
          </div>
        </div>
        {warning ? <p className="text-xs text-warning">{warning}</p> : null}
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button variant="outline" className="min-h-14 flex-1" onClick={() => void calibrate()} disabled={calibrating || !fromDraft(draft)}>
            {calibrating ? <Loader2 className="size-4 animate-spin" /> : <FileText className="size-4" />}
            <span className="ml-1.5">Пробная страница</span>
          </Button>
          {!readOnly && changedFromDefault ? (
            <Button variant="ghost" className="min-h-14" onClick={() => onChange(toDraft(sheet.defaults))}>
              <RotateCcw className="size-4" />
              <span className="ml-1.5">Как по умолчанию</span>
            </Button>
          ) : null}
        </div>
        <p className="text-xs text-on-surface-variant">
          Положите пробную страницу на лист самоклейки и посмотрите на просвет.
        </p>
      </div>
    </section>
  );
}
